import { motion } from "framer-motion";
import { Camera } from "lucide-react";
import { SectionHeading } from "../../components/shared/SectionHeading";
import { IMAGES } from "../../data/mockData";
import { usePageMedia } from "../../lib/usePageMedia";
import { usePageTexts } from "../../lib/usePageTexts";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.55, ease: "easeOut" } }),
};

const CHAPTERS = [
  {
    id: "decoration",
    title: "D'abord la décoration",
    paragraphs: [
      "L'aventure a commencé avec la location de décoration et de matériel événementiel.",
      "Au fil des mariages, le catalogue s'est développé en suivant les envies de nos clients, les nouvelles tendances et surtout les demandes que nous rencontrions réellement sur le terrain.",
      "De nouvelles décorations, du mobilier, des jeux en bois, de nouvelles ambiances… avec toujours la même volonté : permettre à chaque couple d'imaginer une réception qui lui ressemble.",
      "Mais progressivement, nos échanges avec les mariés ont dépassé la simple location de matériel.",
    ],
  },
  {
    id: "accompagnement",
    title: "De la décoration à l'accompagnement",
    paragraphs: [
      "Une question sur l'organisation. Un conseil sur un prestataire. Une hésitation sur le déroulement de la journée. Un besoin d'être rassuré à quelques semaines du mariage…",
      "Petit à petit, les couples ont commencé à nous solliciter bien au-delà de la décoration.",
      "C'est ainsi que la coordination de mariage est devenue une évidence.",
      "Non pas pour imposer notre façon de faire, mais au contraire pour mettre notre expérience du terrain au service des mariés : les écouter, les conseiller, anticiper et les accompagner pour construire une journée qui leur ressemble réellement.",
    ],
  },
  {
    id: "famille",
    title: "Une aventure familiale",
    paragraphs: [
      "Si Art Event porte aujourd'hui ma vision, cette aventure n'est pas solitaire.",
      "À mes côtés, il y a ma mère, mon frère et plusieurs personnes très proches de moi qui répondent présents lorsque les week-ends deviennent plus intenses.",
      "Cette dimension familiale fait partie de notre manière de travailler : rester accessibles, impliqués et proches des personnes qui nous confient une journée aussi importante.",
    ],
  },
];

const NOUVEAU_CHAPITRE = [
  "Ce dont je suis probablement le plus fier, c'est de voir Art Event grandir année après année sans perdre ce qui m'a donné envie de le créer : le plaisir, l'imagination et la créativité.",
  "Six ans après sa création, Art Event franchit aujourd'hui une nouvelle étape.",
  "Un univers de location qui continue de s'enrichir, un accompagnement des mariés qui prend une place toujours plus importante et désormais de nouveaux outils pensés pour rendre l'organisation et la préparation de chaque événement encore plus simples.",
];

const MOT_DE_THEO = `Derrière chaque mariage, il y a avant tout une rencontre.
J'aime prendre le temps de comprendre les envies des couples que nous accompagnons, leur univers et ce qui compte réellement pour eux.
Mon rôle n'est pas de décider à leur place, mais de mettre mon expérience, mes idées et ma créativité au service de leur projet.
Parce qu'au final, le plus beau résultat reste de voir un mariage prendre vie exactement comme ils l'avaient imaginé.`;

function Chapter({ chapter, index }) {
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      custom={0}
      variants={fadeUp}
      className="mx-auto max-w-3xl px-2 py-14 sm:py-20"
      data-testid={`histoire-chapter-${chapter.id}`}
    >
      <p className="font-accent text-2xl text-gold sm:text-3xl">{String(index + 2).padStart(2, "0")}.</p>
      <h2 className="mt-2 font-heading text-2xl text-deepgreen sm:text-3xl">{chapter.title}</h2>
      <div className="mt-6 space-y-5">
        {chapter.paragraphs.map((p, i) => (
          <p key={i} className="text-base leading-relaxed text-foreground/75 sm:text-lg sm:leading-loose">
            {p}
          </p>
        ))}
      </div>
    </motion.section>
  );
}

export default function HistoirePage() {
  const pageMedia = usePageMedia();
  const { text } = usePageTexts();
  const heroMedia = pageMedia["mon-histoire:hero"];
  const theoMedia = pageMedia["mon-histoire:portrait-theo"];
  return (
    <div data-testid="histoire-page">
      <div className="container-ae pt-12 md:pt-16">
        <SectionHeading overline="À propos" title="Mon" accent="histoire">
          {text("mon-histoire:intro", "Derrière Art Event, une passion : créer des événements qui ressemblent à ceux qui les vivent.")}
        </SectionHeading>
      </div>

      {/* Chapitre 1 — naissance sur le terrain */}
      <section className="container-ae mt-4" data-testid="histoire-chapter-terrain">
        <div className="grid items-center gap-10 py-14 sm:py-20 lg:grid-cols-2 lg:gap-16">
          <motion.div initial="hidden" animate="visible" custom={0} variants={fadeUp}>
            <img
              src={heroMedia?.url || IMAGES.gazebo}
              alt={heroMedia?.alt || "Art Event — Mon histoire"}
              className="w-full rounded-xl object-cover shadow-md"
              loading="lazy"
            />
            {!heroMedia && (
              <div className="mt-3">
              </div>
            )}
          </motion.div>
          <motion.div initial="hidden" animate="visible" custom={1} variants={fadeUp}>
            <p className="font-accent text-2xl text-gold sm:text-3xl">01.</p>
            <h2 className="mt-2 font-heading text-2xl text-deepgreen sm:text-3xl">Une histoire née sur le terrain</h2>
            <div className="mt-6 space-y-5">
              <p className="font-heading text-lg leading-relaxed text-deepgreen sm:text-xl">
                Art Event est né il y a six ans d'un constat simple.
              </p>
              <p className="text-base leading-relaxed text-foreground/75 sm:text-lg sm:leading-loose">
                En travaillant quotidiennement dans l'univers de l'événementiel et au sein de différents domaines de réception, j'ai rencontré de nombreux couples à la recherche de prestataires capables de les accompagner dans leurs projets, notamment dans le Valenciennois et le Maubeugeois.
              </p>
              <p className="text-base leading-relaxed text-foreground/75 sm:text-lg sm:leading-loose">
                Des mariés avec des idées, des envies, parfois beaucoup de questions… mais pas toujours les bonnes adresses ou les bons interlocuteurs à proximité.
              </p>
              <p className="font-heading text-lg text-deepgreen sm:text-xl">C'est de ce constat qu'est né Art Event.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Chapitres 2 à 4 — rythme de lecture aéré */}
      <div className="divide-y divide-border/60">
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.id} chapter={c} index={i} />
        ))}
      </div>

      {/* Chapitre 5 — nouveau chapitre + philosophie */}
      <section className="bg-sage-light" data-testid="histoire-chapter-chapitre">
        <div className="container-ae">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            className="mx-auto max-w-3xl px-2 py-16 sm:py-24"
          >
            <p className="font-accent text-2xl text-gold sm:text-3xl">05.</p>
            <h2 className="mt-2 font-heading text-2xl text-deepgreen sm:text-3xl">Six ans plus tard, un nouveau chapitre</h2>
            <div className="mt-6 space-y-5">
              {NOUVEAU_CHAPITRE.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-foreground/75 sm:text-lg sm:leading-loose">{p}</p>
              ))}
            </div>
            <p className="mt-6 text-base leading-relaxed text-foreground/75 sm:text-lg sm:leading-loose">
              Mais malgré cette évolution, notre philosophie reste la même :
            </p>
            <blockquote
              className="mt-10 border-l-4 border-gold bg-white/70 px-6 py-6 sm:px-10 sm:py-8"
              data-testid="histoire-philosophie-quote"
            >
              <p className="font-accent text-2xl leading-snug text-deepgreen sm:text-3xl">
                « Votre mariage ne doit pas ressembler à celui des autres. Il doit vous ressembler. »
              </p>
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Un mot de Théo — emplacement photo réservé (aucune fausse photo générée) */}
      <section className="container-ae" data-testid="histoire-mot-theo">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="grid items-center gap-10 py-16 sm:py-24 lg:grid-cols-5 lg:gap-16"
        >
          <div className="lg:col-span-2">
            {theoMedia ? (
              <img
                src={theoMedia.url}
                alt={theoMedia.alt || "Théo — Fondateur d'Art Event"}
                className="mx-auto aspect-[4/5] w-full max-w-sm rounded-xl object-cover shadow-md lg:mx-0"
                loading="lazy"
                data-testid="histoire-theo-photo"
              />
            ) : (
              <div
                className="flex aspect-[4/5] w-full max-w-sm flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-sage bg-sage-light/60 p-8 text-center mx-auto lg:mx-0"
                data-testid="histoire-theo-photo-placeholder"
              >
                <Camera className="h-8 w-8 text-sage" aria-hidden />
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-deepgreen">Portrait de Théo</p>
                <p className="text-xs text-muted-foreground">Photo à venir — fournie par Art Event</p>
              </div>
            )}
          </div>
          <div className="lg:col-span-3">
            <p className="overline-ae">Un mot de Théo</p>
            <blockquote className="mt-5 space-y-5" data-testid="histoire-theo-quote">
              {MOT_DE_THEO.split("\n").map((line, i) => (
                <p key={i} className="font-heading text-lg italic leading-relaxed text-deepgreen/90 sm:text-xl sm:leading-relaxed">
                  {line}
                </p>
              ))}
            </blockquote>
            <p className="mt-8 font-accent text-2xl text-gold sm:text-3xl" data-testid="histoire-theo-signature">
              Théo — Fondateur d'Art Event
            </p>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
